/**
 * Bounds of a set of canvas nodes, for the "Fit view" fallback.
 *
 * React Flow's own fitView() silently does nothing until EVERY node has been
 * measured (width/height known), so the control can look dead. When it declines,
 * we fit explicit bounds computed from node positions instead — falling back to a
 * typical card size for any node that hasn't been measured yet.
 */

export interface FitNode {
  position: { x: number; y: number };
  width?: number | null;
  height?: number | null;
}

export interface FitRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Typical rendered node card size, used only for nodes with no measurement yet. */
export const DEFAULT_NODE_W = 240;
export const DEFAULT_NODE_H = 110;

/** Smallest rect containing every node, or null when there are none. */
export function boundsOfNodes(nodes: FitNode[]): FitRect | null {
  if (!nodes.length) return null;
  const minX = Math.min(...nodes.map((n) => n.position.x));
  const minY = Math.min(...nodes.map((n) => n.position.y));
  const maxX = Math.max(...nodes.map((n) => n.position.x + (n.width ?? DEFAULT_NODE_W)));
  const maxY = Math.max(...nodes.map((n) => n.position.y + (n.height ?? DEFAULT_NODE_H)));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
